/** @format */

import {
  AdditionalData,
  BreadcrumbData,
  EvergreenTemplateData,
  GamData,
  SlotOnLoadDetail,
} from './google-ad-manager.interface';
import { AnalyticsService, Logger, UtilityService, WINDOW } from '@shared/data-access/services';
import { CDN_BASE_URL, PLACEHOLDER_TEMPLATE_DATA } from './google-ad-manager.constants';
import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  TransferState,
  computed,
  inject,
  input,
  makeStateKey,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformServer } from '@angular/common';
import {
  HomefieldIconLibraryModule,
  HomefieldIconsLibraryService,
  homefieldIconAlertInfoOutline,
  homefieldIconFulfillmentAvailabilityFill,
} from '@dsg-tech/homefield-angular-icons';

import { Chain } from '@shared/data-access/interfaces';
import { EvergreenTemplateComponent } from '../evergreen-template/evergreen-template.component';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'shared-components-google-ad-manager',
  templateUrl: './google-ad-manager.component.html',
  styleUrl: './google-ad-manager.component.scss',
  imports: [CommonModule, HomefieldIconLibraryModule, EvergreenTemplateComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoogleAdManagerComponent implements OnInit, OnDestroy {
  readonly data = input.required<string>();
  readonly breadcrumbData = input<BreadcrumbData | undefined>(undefined);
  readonly addlData = input<AdditionalData | null>(null);
  readonly showSponsoredLabel = input<boolean>(false);
  readonly hasTopMargin = input(true);
  readonly enableTooltipSurvey = input(true);

  readonly gamData = signal<GamData | undefined>(undefined);
  readonly hideAdLabel = signal<boolean>(true);

  // Computed signals derived from inputs
  readonly showSponsoredLabelUpdated = computed(
    () => this.addlData()?.data[0]?.showSponsoredLabel ?? this.showSponsoredLabel(),
  );

  readonly shouldShowSponsoredLabel = computed(
    () => this.showSponsoredLabelUpdated() && !this.hideAdLabel(),
  );

  readonly isTopPlacement = computed(
    () => this.data() === 'top_narrow_banner' || this.data() === 'top_ribbon_banner',
  );

  /* Ad Carousel variables */
  readonly enableAdCarousel = input(false);
  readonly isNarrowBanner = input<boolean>(false);
  readonly currentSlide = signal<number>(0);
  readonly carouselBaseUrl = CDN_BASE_URL;
  readonly evergreenCampaign = signal<number>(0);
  readonly placementName = computed(() => (this.isNarrowBanner() ? 'narrow' : 'ribbon'));
  readonly templateData = signal<EvergreenTemplateData | null>(null);
  readonly pageType = input<string>('');

  private readonly analyticsService = inject(AnalyticsService);
  protected readonly window = inject(WINDOW);
  private readonly chain = inject(Chain);
  private readonly iconsLib = inject(HomefieldIconsLibraryService);
  private readonly utilityService = inject(UtilityService);
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly transferState = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly ngZone = inject(NgZone);

  readonly isDsgStorePage =
    this.window.location?.href.endsWith('/s/stores') && this.chain.chainIdentifierAbbr === 'dsg';

  readonly EVERGREEN_DATA_KEY = makeStateKey<EvergreenTemplateData>('evergreenTemplateDataKey');

  private boundHandleSlotOnLoad: EventListener | undefined;

  constructor() {
    this.iconsLib.registerIcons([
      homefieldIconAlertInfoOutline,
      homefieldIconFulfillmentAvailabilityFill,
    ]);
  }

  ngOnInit(): void {
    if (this.enableAdCarousel()) {
      this.loadEvergreenContent();
    }
    this.utilityService.deferOnPrerendering(() => this.triggerAnalytics());

    this.setupSlotOnLoadListener();
  }

  ngOnDestroy(): void {
    if (this.boundHandleSlotOnLoad) {
      this.window?.document?.removeEventListener('GAM_slotOnload', this.boundHandleSlotOnLoad);
    }
  }

  private triggerAnalytics(): void {
    const analyticsEventObject: { AdSpotIDs: string; Breadcrumbs?: BreadcrumbData } = {
      AdSpotIDs: this.data(),
    };
    // add breadcrumbs data to analytics event if category page has breadcrumbs
    if (this.breadcrumbData()) {
      analyticsEventObject.Breadcrumbs = this.breadcrumbData();
    }

    this.analyticsService.triggerAnalyticsEvent('AdSpotsReady', analyticsEventObject);
  }

  loadSurvey(): void {
    if (this.window.KAMPYLE_ONSITE_SDK !== undefined && this.enableTooltipSurvey()) {
      this.window.gamData = this.gamData();
      if (this.chain.chainIdentifierAbbr === 'dsg') {
        this.window.KAMPYLE_ONSITE_SDK.showForm(15821);
      } else if (this.chain.chainIdentifierAbbr === 'gg') {
        this.window.KAMPYLE_ONSITE_SDK.showForm(16385);
      }
    }
  }

  private setupSlotOnLoadListener(): void {
    if (!this.window?.document?.addEventListener) return;

    this.ngZone.runOutsideAngular(() => {
      this.boundHandleSlotOnLoad = (event: Event) =>
        this.handleSlotOnLoad(event as CustomEvent<SlotOnLoadDetail>);
      if (this.boundHandleSlotOnLoad) {
        this.window.document.addEventListener('GAM_slotOnload', this.boundHandleSlotOnLoad);
      }
    });
  }

  handleSlotOnLoad(event: CustomEvent<SlotOnLoadDetail>): void {
    if (this.data() !== event.detail?.AdUnitId) return;

    this.ngZone.run(() => {
      this.hideAdLabel.set(false);

      if (this.enableAdCarousel()) {
        this.currentSlide.set(1);
      }

      this.gamData.set({ Header: `creativeId: ${event.detail.creativeId}` });
    });
  }

  private getEvergreenJson(): Observable<EvergreenTemplateData> {
    return this.http
      .get<EvergreenTemplateData>(
        `${this.carouselBaseUrl}/${this.chain.chainIdentifierAbbr}/${this.placementName()}/${this.placementName()}_${this.evergreenCampaign()}.json`,
      )
      .pipe(
        map((templateData: EvergreenTemplateData) => {
          templateData.MAIN_IMG = `${this.carouselBaseUrl}/${this.chain.chainIdentifierAbbr}/${this.placementName()}/images/${templateData.MAIN_IMG}`;
          return templateData;
        }),
      );
  }

  /**
   * Loads evergreen content using TransferState to avoid duplicate fetches.
   * On server (and Search): fetches JSON and stores in TransferState
   * On client: uses cached data from TransferState
   */
  private loadEvergreenContent() {
    const cachedEvergreenData = this.transferState.get(this.EVERGREEN_DATA_KEY, null);

    if (cachedEvergreenData !== null && this.pageType() !== 'srlp') {
      this.templateData.set(cachedEvergreenData);
      this.transferState.remove(this.EVERGREEN_DATA_KEY); // so transferstate data is not reused on other pages
      return;
    }

    this.templateData.set(PLACEHOLDER_TEMPLATE_DATA); // for skeleton loader on search
    this.evergreenCampaign.set(Math.floor(Math.random() * 5));

    this.getEvergreenJson().subscribe({
      next: (templateData) => {
        this.templateData.set(templateData);
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(this.EVERGREEN_DATA_KEY, templateData);
        }
      },
      error: (error) => {
        this.logger.error('Error loading Evergreen JSON:', error);
      },
    });
  }
}
